// Sign-in. The design is PIN-only; the mode chooser, credentials form, TOTP
// step and roster grid are net-new (the README's auth model), drawn in the
// spec's own visual grammar.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { ApiError } from '@/api/http';
import type { OperatorVM, SessionInfo } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Flag, Icon, PodMark } from '@/components/ui/Icon';
import { NumberPad, PinDots } from '@/components/ui/NumberPad';
import { AvatarTile, EmptyState, SectionLabel, Skeleton } from '@/components/ui/misc';
import { InstallBanner } from '@/components/ui/InstallBanner';
import { useT } from '@/i18n/useT';
import { useCompletedToday, useWaitingCounts } from '@/queries';
import { useStore } from '@/store';
import { isTablet } from '@/store/slices/ui';

export function LoginScreen({ onAuthenticated }: { onAuthenticated: (s: SessionInfo) => void }) {
  const t = useT();
  const vw = useStore((s) => s.vw);
  const board = isTablet(vw);
  const stage = useStore((s) => s.authStage);
  const setStage = useStore((s) => s.setAuthStage);
  const setAuthMethod = useStore((s) => s.setAuthMethod);
  const kioskAvailable = useStore((s) => s.connMode) !== 'none' || true;

  const body = (() => {
    switch (stage) {
      case 'credentials':
      case 'totp':
        return <CredentialsFlow onAuthenticated={onAuthenticated} />;
      case 'roster':
        return <RosterGrid board={board} />;
      case 'pin':
      case 'welcome':
        return <PinStep onAuthenticated={onAuthenticated} />;
      case 'chooser':
      default:
        return (
          <ModeChooser
            onPick={(m) => {
              setAuthMethod(m);
              setStage(m === 'credentials' ? 'credentials' : 'roster');
            }}
            kioskAvailable={kioskAvailable}
          />
        );
    }
  })();

  if (board) {
    return (
      <div style={{ display: 'flex', minHeight: 'calc(100dvh - var(--sf-banner, 0px))', animation: 'ipFade .3s ease' }}>
        <BoardBrandPanel />
        <div
          style={{
            width: 460,
            flex: 'none',
            background: 'var(--bg)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: 40,
          }}
        >
          {/* On the sign-in screen rather than in the app: installing a device
              is a setup decision, and this is where setup happens. */}
          <InstallBanner />
          {body}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: 'calc(100dvh - var(--sf-banner, 0px))',
        background: 'var(--bg)',
        padding: '14px 26px 26px',
        display: 'flex',
        flexDirection: 'column',
        animation: 'ipFade .3s ease',
      }}
    >
      <PhoneHeader />
      <InstallBanner />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>{body}</div>
      <p style={{ font: '500 10.5px Manrope', color: 'var(--sub)', textAlign: 'center', margin: 0 }}>
        {t.station} · Interpod
      </p>
    </div>
  );
}

function PhoneHeader() {
  const cycle = useStore((s) => s.cycleLanguage);
  const lang = useStore((s) => s.language);
  return (
    <header style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <PodMark size={26} style={{ color: 'var(--ink)' }} />
      <span style={{ font: "600 18px 'Space Grotesk',sans-serif", letterSpacing: '-.01em' }}>interpod</span>
      <button
        type="button"
        className="press"
        onClick={cycle}
        style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          minHeight: 44,
          padding: '0 12px',
          borderRadius: 22,
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          font: '600 12px Manrope,sans-serif',
        }}
      >
        <Flag lang={lang} />
        {lang} ▾
      </button>
    </header>
  );
}

function BoardBrandPanel() {
  const t = useT();
  const wc = useStore((s) => s.workcentreName);
  const wcId = useStore((s) => s.workcentreId);
  const fl = useStore((s) => s.flowlineName);
  const flId = useStore((s) => s.flowlineId);
  const counts = useWaitingCounts(flId);
  const completed = useCompletedToday();
  const waiting = wcId != null ? counts.data?.[wcId] ?? null : null;
  const done = completed.data?.length ?? 0;
  return (
    <div
      style={{
        flex: 1,
        background: 'var(--accent)',
        color: 'var(--accentInk)',
        padding: 48,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <PodMark size={40} />
        <span style={{ font: "600 26px 'Space Grotesk',sans-serif" }}>interpod</span>
      </div>
      <div>
        <div
          role="presentation"
          style={{ font: "700 44px/1.05 'Space Grotesk',sans-serif", letterSpacing: '-.02em' }}
        >
          {wc ?? t.station}
        </div>
        <p style={{ font: '500 18px Manrope', opacity: 0.9, margin: '10px 0 0' }}>
          {fl ? `${fl} · ${t.stationBoard}` : t.stationBoard}
        </p>
        <p style={{ font: '500 14px Manrope', opacity: 0.75, maxWidth: 360, marginTop: 18 }}>{t.signInPickPod}</p>
      </div>
      {/* The station's actual load, so someone signing on knows what they are
          walking into. Shown only once the counts are in — a placeholder zero
          would read as "nothing waiting". */}
      <div style={{ font: '500 13px Manrope', opacity: 0.75 }}>
        {waiting != null
          ? `${waiting} ${t.podsWaiting} · ${done} ${t.completedTodayT.toLowerCase()}`
          : 'Interpod Shop Floor'}
      </div>
    </div>
  );
}

function ModeChooser({
  onPick,
  kioskAvailable,
}: {
  onPick: (m: 'pin' | 'credentials') => void;
  kioskAvailable: boolean;
}) {
  const t = useT();
  const card = (icon: React.ReactNode, title: string, sub: string, onClick: () => void, disabled = false) => (
    <button
      type="button"
      className={disabled ? undefined : 'press'}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        width: '100%',
        height: 84,
        padding: '0 18px',
        borderRadius: 16,
        background: 'var(--surface)',
        border: '1.5px solid var(--line)',
        opacity: disabled ? 0.5 : 1,
        textAlign: 'left',
      }}
    >
      <span
        style={{
          width: 44,
          height: 44,
          borderRadius: 14,
          background: 'var(--soft)',
          color: 'var(--accent)',
          display: 'grid',
          placeItems: 'center',
          flex: 'none',
        }}
      >
        {icon}
      </span>
      <span>
        <span style={{ display: 'block', font: "700 14px 'Space Grotesk',sans-serif" }}>{title}</span>
        <span style={{ display: 'block', font: '500 11px Manrope', color: 'var(--sub)', marginTop: 3 }}>{sub}</span>
      </span>
    </button>
  );

  return (
    <div>
      <h1 style={{ font: "700 25px 'Space Grotesk',sans-serif", margin: '0 0 18px' }}>{t.signIn}</h1>
      <div style={{ display: 'grid', gap: 12 }}>
        {card(
          <span style={{ font: "700 17px 'Space Grotesk',sans-serif" }}>•••</span>,
          t.signInOperatorPin,
          t.signInOperatorPinSub,
          () => onPick('pin'),
          !kioskAvailable,
        )}
        {card(<Icon name="person" size={20} />, t.signInOdooAccount, t.signInOdooAccountSub, () =>
          onPick('credentials'),
        )}
      </div>
    </div>
  );
}

function CredentialsFlow({ onAuthenticated }: { onAuthenticated: (s: SessionInfo) => void }) {
  const t = useT();
  const stage = useStore((s) => s.authStage);
  const setStage = useStore((s) => s.setAuthStage);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [features, setFeatures] = useState({ totpRememberDevice: false });

  const loginMut = useMutation({
    mutationFn: () => api.loginCredentials(login, password),
    onSuccess: (res) => {
      if ('totpRequired' in res) {
        setStage('totp');
        setError(null);
        return;
      }
      setFeatures(res.features);
      onAuthenticated(res);
    },
    onError: (e: ApiError) => setError(e.message),
  });

  const totpMut = useMutation({
    mutationFn: () => api.submitTotp(code, remember),
    onSuccess: onAuthenticated,
    onError: (e: ApiError) => {
      setError(e.message);
      setCode('');
    },
  });

  const inputStyle: React.CSSProperties = {
    height: 48,
    borderRadius: 12,
    background: 'var(--surface2)',
    border: '1.5px solid var(--line)',
    padding: '0 14px',
    font: '500 14px Manrope,sans-serif',
    color: 'var(--ink)',
    width: '100%',
  };

  if (stage === 'totp') {
    return (
      <div>
        <h1 style={{ font: "700 22px 'Space Grotesk',sans-serif", margin: '0 0 6px' }}>{t.twoFactor}</h1>
        <p style={{ font: '500 12.5px Manrope', color: 'var(--sub)', margin: '0 0 20px' }}>{t.twoFactorSub}</p>
        <div style={{ marginBottom: 16 }}>
          <PinDots length={6} filled={code.length} error={!!error} />
        </div>
        {error && (
          <p role="alert" style={{ textAlign: 'center', font: '600 11.5px Manrope', color: 'var(--danger)', margin: '0 0 10px' }}>
            {error}
          </p>
        )}
        <NumberPad
          onKey={(d) => {
            const next = (code + d).slice(0, 6);
            setCode(next);
            if (next.length === 6) setTimeout(() => totpMut.mutate(), 120);
          }}
          onDelete={() => setCode((c) => c.slice(0, -1))}
          disabled={totpMut.isPending}
        />
        {features.totpRememberDevice && (
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <Chip kind="select" selected={remember} onClick={() => setRemember((v) => !v)}>
              {t.trustDevice}
            </Chip>
          </div>
        )}
        <button
          type="button"
          className="press"
          onClick={() => setStage('credentials')}
          style={{ display: 'block', margin: '18px auto 0', font: '600 12.5px Manrope', color: 'var(--sub)' }}
        >
          {t.back}
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        loginMut.mutate();
      }}
    >
      <h1 style={{ font: "700 22px 'Space Grotesk',sans-serif", margin: '0 0 18px' }}>{t.signInOdooAccount}</h1>
      <SectionLabel>{t.odooLogin}</SectionLabel>
      <input
        style={{ ...inputStyle, marginBottom: 14 }}
        value={login}
        onChange={(e) => setLogin(e.target.value)}
        autoComplete="username"
        aria-label={t.odooLogin}
      />
      <SectionLabel>{t.password}</SectionLabel>
      <input
        style={inputStyle}
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        aria-label={t.password}
      />
      {error && (
        <p
          role="alert"
          style={{ font: '600 11.5px Manrope', color: 'var(--danger)', marginTop: 12, animation: 'ipShake .4s' }}
        >
          {error}
        </p>
      )}
      <div style={{ marginTop: 20 }}>
        <Button type="submit" pending={loginMut.isPending} disabled={!login || !password}>
          {t.signIn}
        </Button>
      </div>
      <button
        type="button"
        className="press"
        onClick={() => setStage('chooser')}
        style={{ display: 'block', margin: '16px auto 0', font: '600 12.5px Manrope', color: 'var(--accent)' }}
      >
        {t.useDifferentSignIn}
      </button>
    </form>
  );
}

/** Tile geometry. A fixed row height keeps rows aligned as they wrap. */
const TILE_H = 132;
const GAP = 10;
const PHONE_COLS = 3;
const BOARD_COLS = 3;
/** How many rows are visible before it scrolls. Fixes the panel's height. */
const VISIBLE_ROWS = 4;

function RosterGrid({ board }: { board: boolean }) {
  const t = useT();
  const setStage = useStore((s) => s.setAuthStage);
  const setPending = useStore((s) => s.setPendingOperator);
  const workcentreId = useStore((s) => s.workcentreId);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  // A new search is a new shortlist: highlight its first hit, so Enter picks
  // what the operator just narrowed to rather than a stale selection.
  useEffect(() => setActive(0), [q]);

  // Follow the highlight when it walks off the visible columns.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [active]);

  // The kiosk session must exist before the roster can be read — but only
  // establish one if the device does not already have it. Reconnecting on every
  // visit re-authenticates to Odoo needlessly and burns the connect rate limit.
  const connMode = useStore((s) => s.connMode);
  const alreadyConnected = connMode === 'kiosk';
  const connect = useMutation({ mutationFn: () => api.connectKiosk() });
  const setConnMode = useStore((s) => s.setConnMode);

  useEffect(() => {
    if (alreadyConnected) return;
    connect.mutate(undefined, { onSuccess: () => setConnMode('kiosk') });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyConnected]);

  const roster = useQuery({
    queryKey: ['roster'],
    queryFn: () => api.roster(),
    enabled: alreadyConnected || connect.isSuccess,
    staleTime: 5 * 60_000,
  });

  if (connect.isError) {
    const e = connect.error as ApiError;
    return (
      <EmptyState>
        <div style={{ font: "700 15px 'Space Grotesk',sans-serif", color: 'var(--ink)', marginBottom: 6 }}>
          {t.cantReachServer}
        </div>
        <div style={{ marginBottom: 14 }}>{e.message}</div>
        <Button size="md" variant="surface" full={false} onClick={() => connect.mutate()}>
          {t.retry}
        </Button>
        <button
          type="button"
          className="press"
          onClick={() => setStage('chooser')}
          style={{ display: 'block', margin: '14px auto 0', font: '600 12.5px Manrope', color: 'var(--accent)' }}
        >
          {t.useDifferentSignIn}
        </button>
      </EmptyState>
    );
  }

  const all = roster.data ?? [];
  const matched = q ? all.filter((o) => o.name.toLowerCase().includes(q.toLowerCase())) : all;
  // People assigned to this station come first, but as ordering rather than as
  // a separate section: a heading that appears and disappears while you type
  // shifts everything below it. The tile marks them instead.
  const atStation = (o: OperatorVM) => !!workcentreId && !!o.workcentreIds?.includes(workcentreId);
  const filtered = workcentreId
    ? [...matched].sort((a, b) => Number(atStation(b)) - Number(atStation(a)))
    : matched;

  const choose = (o: OperatorVM) => {
    setPending(o);
    setStage('pin');
  };

  const tile = (o: OperatorVM, i: number) => {
    const isActive = i === active;
    const mine = atStation(o);
    return (
      <button
        key={o.id}
        ref={isActive ? activeRef : undefined}
        id={`op-${o.id}`}
        type="button"
        role="option"
        aria-selected={isActive}
        className="press"
        // Focus stays in the search box and moves the highlight from there
        // (the listbox pattern), so typing and choosing are the same gesture.
        tabIndex={-1}
        onClick={() => choose(o)}
        style={{
          // A fixed height keeps rows even as they wrap; the width comes from
          // the column so the grid fills whatever it is given.
          width: '100%',
          height: TILE_H,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 6,
          padding: 12,
          borderRadius: 16,
          background: isActive ? 'var(--soft)' : 'var(--surface)',
          border: `1.5px solid ${isActive ? 'var(--accent)' : mine ? 'var(--accent)' : 'var(--line)'}`,
          borderWidth: isActive ? 2 : 1.5,
          overflow: 'hidden',
        }}
      >
        <AvatarTile name={o.name} size={52} photoUrl={o.avatarUrl} style={{ borderRadius: 16 }} />
        <span
          style={{
            font: '600 12px Manrope',
            textAlign: 'center',
            lineHeight: 1.2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {o.name}
        </span>
        {o.jobTitle && (
          <span
            style={{
              font: '500 9.5px Manrope',
              color: 'var(--sub)',
              textAlign: 'center',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {o.jobTitle}
          </span>
        )}
      </button>
    );
  };

  const cols = board ? BOARD_COLS : PHONE_COLS;
  // Height is computed, not flexed, so the heading, the search box and the
  // footer sit in exactly the same place whether the list holds two hundred
  // people or one. Nothing below the search box moves while you type.
  const boxHeight = VISIBLE_ROWS * TILE_H + (VISIBLE_ROWS - 1) * GAP;

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!filtered.length) return;
    // The list flows left-to-right then wraps, so right/left steps one tile and
    // down/up moves a whole row. The keys match what is on screen.
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: cols, ArrowUp: -cols }[e.key];
    if (step != null) {
      e.preventDefault();
      setActive((i) => Math.max(0, Math.min(filtered.length - 1, i + step)));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const picked = filtered[active] ?? filtered[0];
      if (picked) choose(picked);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <h1 style={{ font: "700 22px 'Space Grotesk',sans-serif", margin: '0 0 12px' }}>{t.chooseYourName}</h1>
      <input
        ref={searchRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={onSearchKey}
        placeholder={t.searchName}
        aria-label={t.searchName}
        role="combobox"
        aria-expanded={filtered.length > 0}
        aria-controls="roster-listbox"
        {...(filtered[active] ? { 'aria-activedescendant': `op-${filtered[active].id}` } : {})}
        autoComplete="off"
        // The roster is long and every operator knows their own name, so
        // typing is the fast path. Land in the box ready for it.
        autoFocus
        style={{
          flex: 'none',
          height: 44,
          borderRadius: 12,
          background: 'var(--surface2)',
          border: '1.5px solid var(--line)',
          padding: '0 14px',
          font: '500 13.5px Manrope,sans-serif',
          color: 'var(--ink)',
          marginBottom: 12,
        }}
      />

      <div
        ref={stripRef}
        style={{
          flex: 'none',
          height: boxHeight,
          overflowY: 'auto',
          // One axis only. Sideways scrolling on top of vertical made it easy
          // to lose your place in two hundred faces.
          overflowX: 'hidden',
        }}
      >
        {(!alreadyConnected && connect.isPending) || roster.isPending ? (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gridAutoRows: `${TILE_H}px`, gap: GAP }}>
            {Array.from({ length: cols * VISIBLE_ROWS }, (_, i) => (
              <Skeleton key={i} height={TILE_H} radius={16} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState>{t.noPodsMatch}</EmptyState>
        ) : (
          <div
            id="roster-listbox"
            role="listbox"
            aria-label={t.chooseYourName}
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gridAutoRows: `${TILE_H}px`,
              gap: GAP,
              alignContent: 'start',
            }}
          >
            {filtered.map(tile)}
          </div>
        )}
      </div>

      <button
        type="button"
        className="press"
        onClick={() => setStage('chooser')}
        style={{ margin: '14px auto 0', font: '600 12.5px Manrope', color: 'var(--accent)', flex: 'none' }}
      >
        {t.useDifferentSignIn}
      </button>
    </div>
  );
}

function PinStep({ onAuthenticated }: { onAuthenticated: (s: SessionInfo) => void }) {
  const t = useT();
  const vw = useStore((s) => s.vw);
  const board = isTablet(vw);
  const pending = useStore((s) => s.pendingOperator);
  const lastOp = useStore((s) => s.lastOperator);
  const pin = useStore((s) => s.pin);
  const pushPin = useStore((s) => s.pushPin);
  const popPin = useStore((s) => s.popPin);
  const error = useStore((s) => s.loginError);
  const setError = useStore((s) => s.setLoginError);
  const setOperator = useStore((s) => s.setOperator);
  const setStage = useStore((s) => s.setAuthStage);

  const who = pending ?? (lastOp ? { id: lastOp.id, name: lastOp.name } : null);

  const confirm = useMutation({
    mutationFn: (value: string) => api.pinConfirm(who!.id, value),
    onSuccess: async (res) => {
      setOperator(res.operator);
      const info = await api.sessionInfo();
      onAuthenticated(info);
    },
    onError: (e: ApiError) => setError(e.message),
  });

  /**
   * One PIN, one attempt.
   *
   * A full PIN submits itself after a beat, and Enter submits too — so without
   * this guard, pressing Enter inside that beat sends the same PIN twice and
   * spends two tries from the lockout ladder for one entry. The ladder is the
   * only PIN throttle in the stack, so double-spending it is not cosmetic.
   */
  const submittedFor = useRef<string | null>(null);
  const submit = (value: string) => {
    if (!who || confirm.isPending || submittedFor.current === value) return;
    submittedFor.current = value;
    confirm.mutate(value);
  };
  // A changed PIN is a new attempt, so let it be submitted again.
  useEffect(() => {
    if (pin.length < 4) submittedFor.current = null;
  }, [pin]);

  useEffect(() => {
    if (pin.length === 4 && who && !confirm.isPending) {
      const value = pin;
      // The pause lets the fourth dot land before the screen changes.
      setTimeout(() => submit(value), 140);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, who]);

  /**
   * The pad is for gloves; the keyboard is for anyone at a desk or on a device
   * with one. Both drive the same handlers, so there is one way the PIN is
   * built and one place it is submitted.
   */
  const pending2 = confirm.isPending;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (pending2) return;
      // Leave browser and OS shortcuts alone.
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;

      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        pushPin(e.key);
        return;
      }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        popPin();
        return;
      }
      // Four digits already submit themselves; Enter is here so that habit
      // works too. `submit` is idempotent per PIN, so the two cannot both fire.
      if (e.key === 'Enter' && pin.length === 4) {
        e.preventDefault();
        submit(pin);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, who, pending2, pushPin, popPin]);

  if (!who) {
    setStage('roster');
    return null;
  }

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 22 }}>
        <AvatarTile name={who.name} size={76} style={{ borderRadius: 24 }} />
        <p style={{ font: '500 13px Manrope', color: 'var(--sub)', margin: '14px 0 2px' }}>
          {pending ? t.enterPin : t.welcomeBack}
        </p>
        <p style={{ font: "700 25px 'Space Grotesk',sans-serif", margin: 0 }}>
          {pending ? who.name : who.name.split(' ')[0]}
        </p>
      </div>
      <div style={{ marginBottom: 12 }}>
        <PinDots length={4} filled={pin.length} error={!!error} size={board ? 'board' : 'phone'} />
      </div>
      {error && (
        <p role="alert" style={{ textAlign: 'center', font: '600 11.5px Manrope', color: 'var(--danger)', margin: '0 0 10px' }}>
          {error}
        </p>
      )}
      <NumberPad
        size={board ? 'board' : 'phone'}
        onKey={pushPin}
        onDelete={popPin}
        disabled={confirm.isPending}
      />
      <button
        type="button"
        className="press"
        onClick={() => setStage('roster')}
        style={{ display: 'block', margin: '18px auto 0', font: '600 12.5px Manrope', color: 'var(--accent)' }}
      >
        {t.notYou}
      </button>
    </div>
  );
}
